import config from '../../config';
import { createAuthenticationError, getAuthHeaders, handleResponse } from './common';

// Throws an Error with .status and .body (parsed JSON when possible) so callers
// can branch on HTTP status. Mirrors the pattern in completeAssignmentInOffice
// below and other APIs (releasePacketApi, transferApi, ultrasoundOrderApi).
const handleStructuredResponse = async (response) => {
  if (response.ok) {
    if (response.status === 204) return { success: true };
    const ct = response.headers.get('content-type');
    if (ct && ct.includes('application/json')) return response.json();
    return { success: true };
  }
  if (response.status === 401) {
    throw createAuthenticationError(response);
  }
  const text = await response.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text || null; }
  const message = (body && typeof body === 'object' && body.message)
    ? body.message
    : (text || `HTTP error! status: ${response.status}`);
  const err = new Error(message);
  err.status = response.status;
  err.body = body;
  err.code = body && typeof body === 'object' ? body.code : undefined;
  err.responseText = text;
  throw err;
};

export const CONSENT_ERROR_CODES = {
  TEMPLATE_NOT_FOUND: 'TemplateNotFound',
  TEMPLATE_HAS_ASSIGNMENTS: 'TemplateHasAssignments',
  TEMPLATE_HAS_VERSIONS: 'TemplateHasVersions',
  CLIENT_NOT_FOUND: 'ClientNotFound',
  TEMPLATE_ARCHIVED: 'TemplateArchived',
  TEMPLATE_INACTIVE: 'TemplateInactive',
  NO_TEMPLATE_DOCUMENT: 'NoTemplateDocument',
  DUPLICATE_ASSIGNMENT: 'DuplicateAssignment',
  TEMPLATE_UNAVAILABLE: 'TemplateUnavailable',
  ASSIGNMENT_NOT_FOUND: 'AssignmentNotFound',
  CONSENT_ALREADY_SENT: 'ConsentAlreadySent',
  CONSENT_LOCKED: 'ConsentLocked',
  CLIENT_HAS_NO_ACCOUNT: 'ClientHasNoAccount',
  CLIENT_HAS_NO_EMAIL: 'ClientHasNoEmail',
};

export const consentApi = {
  getProviderAddendum: async (referenceId) => {
    const response = await fetch(`${config.api.url}/consent-documents/assignments/${referenceId}/provider-addendum`, { headers: getAuthHeaders(), cache: 'no-store' });
    if (response.status === 404) return null; // API-first rollout; older servers have no addendum route.
    return handleStructuredResponse(response);
  },

  signProviderAddendum: async (referenceId, data) => {
    const response = await fetch(`${config.api.url}/consent-documents/assignments/${referenceId}/provider-addendum`, {
      method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data)
    });
    return handleStructuredResponse(response);
  },

  downloadProviderAddendum: async (referenceId) => {
    const response = await fetch(`${config.api.url}/consent-documents/assignments/${referenceId}/provider-addendum/download`, { headers: getAuthHeaders(), cache: 'no-store' });
    if (!response.ok) return handleStructuredResponse(response);
    return response.blob();
  },

  getMyOrganization: async () => {
    const response = await fetch(`${config.api.url}/organization/my`, { headers: getAuthHeaders() });
    return handleResponse(response);
  },

  // Templates
  getTemplates: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await fetch(
      `${config.api.url}/consent-documents/templates?${params}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getTemplateById: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  createTemplate: async (data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  updateTemplate: async (referenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  uploadDocument: async (referenceId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('velaToken');
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/upload`,
      {
        method: 'POST',
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      }
    );
    return handleResponse(response);
  },

  downloadTemplateDocument: async (referenceId) => {
    const token = localStorage.getItem('velaToken');
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/download`,
      { headers: { "Authorization": `Bearer ${token}` } }
    );
    
    if (!response.ok) throw new Error('Download failed');
    return response.blob();
  },

  // Add overlay field
  addOverlayField: async (referenceId, fieldData) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/overlay-fields`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(fieldData)
      }
    );
    return handleStructuredResponse(response);
  },

  // Update single overlay field (granular - prevents race conditions)
  updateOverlayField: async (referenceId, fieldId, updates) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/overlay-fields/${fieldId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      }
    );
    return handleStructuredResponse(response);
  },

  // Delete overlay field
  deleteOverlayField: async (referenceId, fieldId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/overlay-fields/${fieldId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleStructuredResponse(response);
  },

  createTemplateVersion: async (referenceId, data = {}) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/versions`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  saveEditableContent: async (referenceId, contentHtml) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/editable-content`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        // A brand-new draft may not have editor markup yet. Send an explicit
        // empty string rather than allowing undefined to disappear from JSON.
        body: JSON.stringify({ contentHtml: contentHtml ?? '' })
      }
    );
    return handleStructuredResponse(response);
  },

  publishEditableTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/publish`,
      { method: 'POST', headers: getAuthHeaders() }
    );
    return handleStructuredResponse(response);
  },

  previewEditableTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/preview`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) throw new Error('Preview failed');
    return response.blob();
  },

  archiveTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  unarchiveTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/unarchive`,
      {
        method: 'POST',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  deleteTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  },

  permanentlyDeleteTemplate: async (referenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${referenceId}/permanent`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleStructuredResponse(response);
  },

  // Assignments
  assignToClient: async (templateReferenceId, data, options = {}) => {
    const payload = { ...data };
    if (options.replaceExisting) {
      payload.replaceExisting = true;
    }
    const response = await fetch(
      `${config.api.url}/consent-documents/templates/${templateReferenceId}/assign`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      }
    );
    return handleStructuredResponse(response);
  },

  getClientAssignments: async (clientReferenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/clients/${clientReferenceId}/consents`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getMyAssignments: async () => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/my-assignments`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getAssignmentById: async (assignmentReferenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  sendConsent: async (assignmentReferenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/send`,
      {
        method: 'POST',
        headers: getAuthHeaders()
      }
    );
    return handleStructuredResponse(response);
  },

  updateFieldValue: async (assignmentReferenceId, fieldId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/fields/${fieldId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  saveAssignmentDraft: async (assignmentReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/draft`,
      {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleStructuredResponse(response);
  },

  updateFieldValueByReference: async (assignmentReferenceId, consentOverlayFieldReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/fields/by-reference/${consentOverlayFieldReferenceId}`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleStructuredResponse(response);
  },

  completeAssignment: async (assignmentReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/complete`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  completeAssignmentByReference: async (assignmentReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/complete/by-reference`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleStructuredResponse(response);
  },

  completeAssignmentInOffice: async (assignmentReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/complete-in-office`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    if (!response.ok) {
      if (response.status === 401) {
        throw createAuthenticationError(response);
      }
      const errorText = await response.text();
      const err = new Error(errorText || `HTTP error! status: ${response.status}`);
      err.status = response.status;
      err.responseText = errorText;
      throw err;
    }
    return response.json();
  },

  completeAssignmentInOfficeByReference: async (assignmentReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/complete-in-office/by-reference`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleStructuredResponse(response);
  },

  getFinalizedDocument: async (assignmentReferenceId, { readable = false, signal } = {}) => {
    const token = localStorage.getItem('velaToken');
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/download${readable ? '?readable=true' : ''}`,
      { headers: { "Authorization": `Bearer ${token}` }, signal }
    );
    if (!response.ok) throw new Error('Unable to load the signed document');
    const disposition = response.headers.get('Content-Disposition') || '';
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    const plain = disposition.match(/filename="?([^";]+)"?/i);
    let fileName = plain?.[1] || `consent_${assignmentReferenceId}.pdf`;
    if (encoded) { try { fileName = decodeURIComponent(encoded[1]); } catch {} }
    return {
      blob: await response.blob(),
      fileName,
      // Older APIs return originals without this header. Never label them derivatives.
      isReadableCopy: response.headers.get('X-Consent-Readable-Copy') === 'true',
    };
  },

  downloadFinalized: async (assignmentReferenceId, options = {}) =>
    (await consentApi.getFinalizedDocument(assignmentReferenceId, options)).blob,

  countersignAssignment: async (assignmentReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/countersign`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleResponse(response);
  },

  countersignAssignmentByReference: async (assignmentReferenceId, data) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/countersign/by-reference`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data)
      }
    );
    return handleStructuredResponse(response);
  },

  getPendingAssignments: async () => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/pending`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getAwaitingCountersignature: async () => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/awaiting-countersignature`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getCompletedAssignments: async () => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/completed`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  getDeliveryLogs: async (assignmentReferenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}/delivery-logs`,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  },

  deleteAssignment: async (assignmentReferenceId) => {
    const response = await fetch(
      `${config.api.url}/consent-documents/assignments/${assignmentReferenceId}`,
      {
        method: 'DELETE',
        headers: getAuthHeaders()
      }
    );
    return handleResponse(response);
  }
};
